import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { useCreateCarrierAddOnService } from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { hasHttpStatus } from "@/lib/api-error";
import { isServiceCode, normalizeServiceCode } from "@/lib/service-code";

interface CreatePickupServiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function parseDim(value: string): number | null | undefined {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function mutationError(error: unknown): string {
  if (hasHttpStatus(error, 403)) {
    return "You do not have permission to create pickup services.";
  }
  const response = error as {
    response?: { data?: { error?: string; details?: { message?: string } } };
  };
  return (
    response.response?.data?.details?.message ??
    response.response?.data?.error ??
    "Pickup service could not be created."
  );
}

export function CreatePickupServiceDialog({
  open,
  onOpenChange,
}: CreatePickupServiceDialogProps) {
  const navigate = useNavigate();
  const create = useCreateCarrierAddOnService();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [codeEdited, setCodeEdited] = useState(false);
  const [dimFactorCm, setDimFactorCm] = useState("");
  const [dimFactorInch, setDimFactorInch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submissionLock = useRef(false);
  const pending = submitting || create.isPending;

  const reset = () => {
    setName("");
    setCode("");
    setCodeEdited(false);
    setDimFactorCm("");
    setDimFactorInch("");
    setError(null);
    setSubmitting(false);
    submissionLock.current = false;
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && pending) return;
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submissionLock.current) return;

    setError(null);
    const finalName = name.trim();
    const finalCode = code.trim();
    const cm = parseDim(dimFactorCm);
    const inch = parseDim(dimFactorInch);
    if (!finalName) {
      setError("Enter a pickup service name.");
      return;
    }
    if (!isServiceCode(finalCode)) {
      setError(
        "Use only lowercase letters, digits and underscores for the code."
      );
      return;
    }
    if (cm === undefined || inch === undefined) {
      setError("DIM factors must be positive numbers or blank.");
      return;
    }

    submissionLock.current = true;
    setSubmitting(true);
    try {
      const created = await create.mutateAsync({
        type: "PICKUP",
        code: finalCode,
        name: finalName,
        dimFactorCm: cm,
        dimFactorInch: inch,
      });
      toast.success("Pickup service created.");
      reset();
      onOpenChange(false);
      navigate(`/services/pickup/${created.id}`);
    } catch (caught) {
      const message = mutationError(caught);
      setError(message);
      toast.error(message);
    } finally {
      submissionLock.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Add pickup service"
      presentation="responsive-sheet"
      closeDisabled={pending}
    >
      <form noValidate className="space-y-4" onSubmit={handleSubmit}>
        <label
          htmlFor="pickup-service-name"
          className="block space-y-1 text-sm font-medium"
        >
          <span>Name</span>
          <Input
            id="pickup-service-name"
            aria-label="Name"
            value={name}
            onChange={(event) => {
              const nextName = event.target.value;
              setName(nextName);
              if (!codeEdited) setCode(normalizeServiceCode(nextName));
            }}
            placeholder="Scheduled Pickup"
            disabled={pending}
          />
        </label>
        <label
          htmlFor="pickup-service-code"
          className="block space-y-1 text-sm font-medium"
        >
          <span>Code</span>
          <Input
            id="pickup-service-code"
            aria-label="Code"
            value={code}
            onChange={(event) => {
              setCodeEdited(true);
              setCode(normalizeServiceCode(event.target.value));
            }}
            className="font-mono"
            disabled={pending}
          />
          <span className="block text-xs font-normal text-muted-foreground">
            Lowercase letters, digits, and underscores. Permanent after
            creation.
          </span>
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label
            htmlFor="pickup-service-dim-cm"
            className="block space-y-1 text-sm font-medium"
          >
            <span>DIM factor (cm³)</span>
            <Input
              id="pickup-service-dim-cm"
              aria-label="DIM factor (cm³)"
              inputMode="decimal"
              value={dimFactorCm}
              onChange={(event) => setDimFactorCm(event.target.value)}
              placeholder="6000"
              disabled={pending}
            />
          </label>
          <label
            htmlFor="pickup-service-dim-inch"
            className="block space-y-1 text-sm font-medium"
          >
            <span>DIM factor (in³)</span>
            <Input
              id="pickup-service-dim-inch"
              aria-label="DIM factor (in³)"
              inputMode="decimal"
              value={dimFactorInch}
              onChange={(event) => setDimFactorInch(event.target.value)}
              placeholder="166"
              disabled={pending}
            />
          </label>
        </div>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            size="touch"
            onClick={() => handleOpenChange(false)}
            disabled={pending}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="touch"
            disabled={pending}
            className="w-full sm:w-auto"
          >
            {pending ? "Creating…" : "Create service"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
